import { createTheme } from '@mui/material/styles';

const appContentBGColor = '#fff';
const minContrastRatio = 2;

const hexToRgb = hex => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const replacedHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(replacedHex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] :
    null;
};

const getLuminance = (r, g, b) => {
  const a = [r, g, b].map(v => {
    const c = v / 255;
    return c <= 0.03928 ?
      c / 12.92 :
      ((c + 0.055) / 1.055) ** 2.4;
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

const getContrastRatio = (lighterColor, darkerColor) => {
  const rgb1 = hexToRgb(lighterColor);
  const rgb2 = hexToRgb(darkerColor);
  const lum1 = getLuminance(rgb1[0], rgb1[1], rgb1[2]);
  const lum2 = getLuminance(rgb2[0], rgb2[1], rgb2[2]);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) /
    (darkest + 0.05);
};

let theme = {};

const computeContrastColor = (ratio, secondaryMain) =>
  (ratio > minContrastRatio ? secondaryMain : '#061923');

export const getContrastColor = () =>
  (theme.contrastRatio > minContrastRatio ?
    theme.palette.secondary.main : '#061923');

export default wl => {
  const contrastRatio = getContrastRatio(appContentBGColor, `#${wl.color}`);
  const baseTheme = createTheme({
    // spacing: x => x * 8,
    contrastRatio,
    wl,
    palette: {
      primary: {
        main: '#061923'
      },
      secondary: {
        main: wl?.color ? `#${wl.color}` : '#5eb676'
      }
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        SVGA: 800,
        md: 960,
        XGA: 1024,
        XGAPlus: 1152,
        lg: 1280,
        FWXGA: 1366, // https://en.wikipedia.org/wiki/Graphics_display_resolution
        FWXGAPlus: 1440,
        xl: 1920
      },
      // needed for `withWidth()` in Project
      keys: ['xs', 'sm', 'SVGA', 'md', 'XGA', 'XGAPlus', 'lg', 'FWXGA', 'xl']
    },
    mixins: {
      map: {
        '& > div': {
          height: '100%'
        },
        '@media (max-width: 900px) and (orientation: landscape)': {
          height: 375
        }
      }
    }
  });

  const currentContrastColor =
  computeContrastColor(contrastRatio, baseTheme.palette.secondary.main);

  theme = createTheme(baseTheme, {
    components: {
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: '#0000008a'
            },
            '&.Mui-disabled': {
              color: '#00000042'
            }
          }
        }
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: '#0000008a',
            '&.Mui-checked': {
              color: '#0000008a'
            },
            '&.Mui-disabled': {
              color: '#00000042'
            }
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: '#0000008a'
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: wl?.spacing ? `${wl.spacing(2, 3)}` : '16px 24px'
          },
          spacing: {
            '& > :not(:first-of-type)': {
              marginLeft: wl?.spacing ? `${wl.spacing(2)}` : '16px'
            }
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          textSecondary: {
            color: currentContrastColor
          },
          containedSecondary: {
            color: baseTheme.palette.getContrastText(`#${wl.color}`)
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: currentContrastColor
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8
          }
        }
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            borderRadius: 8
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 8
          }
        }
      },
      MuiStepper: {
        styleOverrides: {
          root: {
            padding: 24
          }
        }
      }
    }
  });

  return theme;
};
